import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import {
  listCoursesAdmin,
  listStudentsAdmin,
  adminStats,
  createCourseAdmin,
  deleteCourseAdmin,
  updateCourseAdmin,
  setEnrollmentStatus,
  grantEnrollmentAdmin,
  type CourseRow,
  type StudentRow,
} from '../../lib/admin';
import GradingQueue from './GradingQueue';
import CommerceAdmin from './CommerceAdmin';
import AnnouncementsAdmin from './AnnouncementsAdmin';
import CertificatesAdmin from './CertificatesAdmin';
import ReviewsAdmin from './ReviewsAdmin';
import ProductsAdmin from './ProductsAdmin';
import { PageHeader, Panel, PanelHead, Stamp, Stat, fieldCls, labelCls, tdCls, thCls } from '../../components/lms/ui';
import { marketingHref } from '../../lib/surface';
import { AdminDashboardSkeleton } from '../../components/lms/Skeletons';

type Tab = 'overview' | 'courses' | 'products' | 'students' | 'grading' | 'commerce' | 'announcements' | 'certificates' | 'reviews';

const tabLabels: Record<Tab, string> = {
  overview: 'Overview',
  courses: 'Programmes',
  products: 'Short Courses',
  students: 'Students',
  grading: 'Grading',
  commerce: 'Commerce',
  announcements: 'Notices',
  certificates: 'Certificates',
  reviews: 'Reviews',
};

const tabOrder: Tab[] = ['overview', 'courses', 'products', 'students', 'grading', 'commerce', 'announcements', 'certificates', 'reviews'];

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  const [stats, setStats] = useState({ students: 0, courses: 0, enrollments: 0, certificates: 0, pendingGrading: 0, revenue: 0 });
  const [courses, setCourses] = useState<(CourseRow & { enrollments: number; modules: number })[]>([]);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [newCourse, setNewCourse] = useState({ title: '', price_value: 150000, category: 'Certificate' });
  const [grant, setGrant] = useState({ userId: '', courseId: '' });
  const [grantMsg, setGrantMsg] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, c, st] = await Promise.all([adminStats(), listCoursesAdmin(), listStudentsAdmin().catch(() => [] as StudentRow[])]);
      setStats(s);
      setCourses(c);
      setStudents(st);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCourse.title.trim()) return;
    setBusy('create');
    setError('');
    try {
      await createCourseAdmin({ title: newCourse.title.trim(), price_value: Number(newCourse.price_value) || 0, category: newCourse.category });
      setNewCourse({ title: '', price_value: 150000, category: 'Certificate' });
      await refresh();
      setTab('courses');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the programme.');
    } finally {
      setBusy('');
    }
  };

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grant.userId || !grant.courseId) return;
    setBusy('grant');
    setGrantMsg('');
    try {
      await grantEnrollmentAdmin(grant.userId, grant.courseId);
      setGrantMsg('Access granted — the student can open the classroom now.');
      setGrant({ ...grant, courseId: '' });
      await refresh();
    } catch (err) {
      setGrantMsg(err instanceof Error ? err.message : 'Could not grant access.');
    } finally {
      setBusy('');
    }
  };

  const courseTitle = (id: string) => courses.find((c) => c.id === id)?.title || id;

  if (loading) {
    return <AdminDashboardSkeleton />;
  }

  return (
    <div>
      <PageHeader
        title="Operations desk"
        lede={`Signed in as ${user?.email || 'administrator'} · Programmes, students, grading and the records that leave the school.`}
        actions={<Link to="/courses" className="lms-btn-secondary">View student register</Link>}
      />

      <div className="mt-7 overflow-x-auto pb-1">
        <div className="flex w-max gap-1 rounded-2xl border border-rule bg-white/85 p-1.5 shadow-sm backdrop-blur" role="tablist" aria-label="Admin console sections">
          {tabOrder.map((t) => {
            const active = tab === t;
            return (
              <button
                key={t}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t)}
                className={`whitespace-nowrap rounded-xl px-3.5 py-2.5 text-[12px] font-semibold transition-all ${
                  active
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20'
                    : 'text-ink-2 hover:bg-blue-50 hover:text-blue-800'
                }`}
              >
                {tabLabels[t]}
                {t === 'grading' && stats.pendingGrading > 0 && (
                  <span
                    className={`tnum ml-1.5 rounded-full px-1.5 py-0.5 font-mono text-[8.5px] ${
                      active ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-700'
                    }`}
                  >
                    {stats.pendingGrading}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-xl border border-danger/25 bg-danger-soft px-3.5 py-2.5 text-[12px] text-danger" role="alert">{error}</p>
      )}

      {tab === 'overview' && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ['Students', stats.students, 'Registered profiles'],
                ['Programmes', stats.courses, 'Published and drafts'],
                ['Enrolments', stats.enrollments, 'Active and revoked'],
                ['Revenue', `₦${stats.revenue.toLocaleString('en-NG')}`, 'Completed Paystack payments'],
                ['Certificates', stats.certificates, 'Issued to date'],
                ['Pending grading', stats.pendingGrading, 'Submissions awaiting review'],
              ].map(([label, value, note]) => (
                <div key={label as string} className="rounded-2xl border border-rule bg-white px-5 py-5 shadow-[0_14px_36px_-34px_rgba(15,40,90,0.3)]">
                  <Stat label={label as string} value={value} note={note as string} />
                </div>
              ))}
            </div>

            <Panel>
              <PanelHead title="The queue" action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">Live counts</span>} />
              <div className="divide-y divide-rule">
                <button onClick={() => setTab('grading')} className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left hover:bg-paper">
                  <span>
                    <span className="block text-[13px] font-medium text-ink">Submissions to grade</span>
                    <span className="mt-0.5 block text-[11.5px] text-ink-2">Student work waiting for a grade or return.</span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="tnum font-mono text-[17px] font-semibold text-ink">{stats.pendingGrading}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-pine">Open</span>
                  </span>
                </button>
                <button onClick={() => setTab('students')} className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left hover:bg-paper">
                  <span>
                    <span className="block text-[13px] font-medium text-ink">Programmes on student accounts</span>
                    <span className="mt-0.5 block text-[11.5px] text-ink-2">Grant access, revoke, or restore an enrolment.</span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="tnum font-mono text-[17px] font-semibold text-ink">{stats.enrollments}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-pine">Open</span>
                  </span>
                </button>
                <button onClick={() => setTab('certificates')} className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left hover:bg-paper">
                  <span>
                    <span className="block text-[13px] font-medium text-ink">Certificates in the register</span>
                    <span className="mt-0.5 block text-[11.5px] text-ink-2">Check a record, revoke, or restore public verification.</span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="tnum font-mono text-[17px] font-semibold text-ink">{stats.certificates}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-pine">Open</span>
                  </span>
                </button>
              </div>
            </Panel>
          </div>

          <aside className="space-y-6">
            <Panel>
              <PanelHead title="Working notes" />
              <ul className="divide-y divide-rule">
                {[
                  ['Grading', 'Grade and return submitted work from the Grading tab.'],
                  ['Programmes', 'Publish a programme, then use Homepage to choose whether it appears in the public featured section.'],
                  ['Notices', 'Only published notices appear in student dashboards.'],
                  ['Certificates', 'A revoked certificate stops verifying in the public register. Keep the reason on record.'],
                ].map(([label, copy]) => (
                  <li key={label} className="px-5 py-3.5">
                    <p className="text-[12.5px] font-semibold text-ink">{label}</p>
                    <p className="mt-0.5 text-[11.5px] leading-5 text-ink-2">{copy}</p>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel>
              <PanelHead title="Desk map" />
              <ul className="divide-y divide-rule">
                {[
                  ['Programmes', 'Create, publish, and author content.'],
                  ['Students', 'Grant or remove classroom access.'],
                  ['Commerce', 'Bundles and promo codes.'],
                  ['Notices', 'Publish student announcements.'],
                ].map(([label, copy]) => (
                  <li key={label} className="px-5 py-3">
                    <p className="text-[12.5px] font-medium text-ink">{label}</p>
                    <p className="mt-0.5 text-[11.5px] leading-5 text-ink-2">{copy}</p>
                  </li>
                ))}
              </ul>
            </Panel>
          </aside>
        </div>
      )}

      {tab === 'courses' && (
        <div className="mt-6 space-y-6">
          <Panel>
            <PanelHead title="Create a programme" action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">New file</span>} />
            <form onSubmit={handleCreate} className="grid gap-4 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_160px_170px_auto] sm:items-end">
              <div>
                <label htmlFor="new-course-title" className={labelCls}>Programme title</label>
                <input id="new-course-title" value={newCourse.title} onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })} placeholder="e.g. Export Documentation Bootcamp" className={fieldCls} />
              </div>
              <div>
                <label htmlFor="new-course-price" className={labelCls}>Price (NGN)</label>
                <input id="new-course-price" type="number" value={newCourse.price_value} onChange={(e) => setNewCourse({ ...newCourse, price_value: Number(e.target.value) })} className={fieldCls} />
              </div>
              <div>
                <label htmlFor="new-course-category" className={labelCls}>Category</label>
                <input id="new-course-category" value={newCourse.category} onChange={(e) => setNewCourse({ ...newCourse, category: e.target.value })} className={fieldCls} />
              </div>
              <button disabled={busy === 'create'} className="lms-btn-primary">
                {busy === 'create' ? 'Creating…' : 'Create'}
              </button>
            </form>
            <p className="border-t border-rule px-5 py-3 text-[11.5px] leading-5 text-ink-2">
              New programmes are featured on the homepage by default. Publish the programme, then use the Homepage toggle
              in the ledger if you want to hide it from the public featured section.
            </p>
          </Panel>

          <Panel className="overflow-x-auto">
            <PanelHead title="Programme ledger" action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{courses.length} records</span>} />
            <table className="w-full min-w-[900px]">
              <thead className="border-b border-rule bg-paper/60">
                <tr>
                  <th className={thCls}>Programme</th>
                  <th className={thCls}>Category</th>
                  <th className={thCls}>Tuition</th>
                  <th className={thCls}>Enrolled</th>
                  <th className={thCls}>Modules</th>
                  <th className={thCls}>Status</th>
                  <th className={thCls}>Homepage</th>
                  <th className={`${thCls} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {courses.map((c) => (
                  <tr key={c.id} className="align-top hover:bg-paper/40">
                    <td className={tdCls}>
                      <p className="font-medium text-ink">{c.title}</p>
                      <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.08em] text-ink-2">{c.id}</p>
                    </td>
                    <td className={tdCls}>{c.category || '—'}</td>
                    <td className={`${tdCls} tnum whitespace-nowrap`}>₦{c.price_value.toLocaleString('en-NG')}</td>
                    <td className={`${tdCls} tnum`}>{c.enrollments}</td>
                    <td className={`${tdCls} tnum`}>{c.modules}</td>
                    <td className={tdCls}>
                      <Stamp tone={c.is_published ? 'cleared' : 'neutral'}>{c.is_published ? 'Published' : 'Draft'}</Stamp>
                    </td>
                    <td className={tdCls}>
                      <button
                        onClick={async () => {
                          setBusy(`home:${c.id}`);
                          setError('');
                          try {
                            await updateCourseAdmin(c.id, { is_featured: !c.is_featured });
                            await refresh();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Could not change homepage visibility.');
                          } finally {
                            setBusy('');
                          }
                        }}
                        disabled={busy === `home:${c.id}`}
                        className="lms-btn-secondary !px-2.5 !py-1.5 !text-[11px]"
                        title="Toggle the public homepage featured section"
                      >
                        {c.is_featured ? 'Featured' : 'Hidden'}
                      </button>
                    </td>
                    <td className={`${tdCls} text-right`}>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <Link to={`/admin/courses/${c.id}`} className="lms-btn-secondary !px-2.5 !py-1.5 !text-[11px]">Edit content</Link>
                        <a
                          href={marketingHref(`/course/${c.id}`)}
                          target="_blank"
                          rel="noreferrer"
                          className="lms-btn-secondary !px-2.5 !py-1.5 !text-[11px]"
                        >
                          View page
                        </a>
                        <button
                          onClick={async () => {
                            setBusy(c.id);
                            setError('');
                            try {
                              await updateCourseAdmin(c.id, { is_published: !c.is_published });
                              await refresh();
                            } catch (err) {
                              setError(err instanceof Error ? err.message : 'Could not change publication state.');
                            } finally {
                              setBusy('');
                            }
                          }}
                          disabled={busy === c.id}
                          className="lms-btn-secondary !px-2.5 !py-1.5 !text-[11px]"
                        >
                          {c.is_published ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(`Delete ${c.title}?`)) return;
                            setBusy(c.id);
                            setError('');
                            try {
                              await deleteCourseAdmin(c.id);
                              await refresh();
                            } catch (err) {
                              setError(err instanceof Error ? err.message : 'Could not delete the programme.');
                            } finally {
                              setBusy('');
                            }
                          }}
                          disabled={busy === c.id}
                          className="lms-btn-danger !px-2.5 !py-1.5 !text-[11px]"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {courses.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-10 text-center text-[12.5px] text-ink-2">
                      No programmes on file. Create the first one above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Panel>
        </div>
      )}

      {tab === 'products' && <ProductsAdmin />}

      {tab === 'students' && (
        <div className="mt-6 space-y-6">
          <Panel>
            <PanelHead title="Grant course access" action={<Stamp tone="neutral">No Paystack charge</Stamp>} />
            <form onSubmit={handleGrant} className="px-5 py-4">
              <p className="max-w-[74ch] text-[12px] leading-5 text-ink-2">
                Comp or manually enrol a student. Use this for scholarships, staff accounts, or a payment that needs to
                be reconciled by the desk.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                <div>
                  <label htmlFor="grant-student" className={labelCls}>Student</label>
                  <select id="grant-student" value={grant.userId} onChange={(e) => setGrant({ ...grant, userId: e.target.value })} className={fieldCls}>
                    <option value="">Select student…</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.id}>{s.full_name || s.email || '(no name)'} · {s.id.slice(0, 8)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="grant-course" className={labelCls}>Programme</label>
                  <select id="grant-course" value={grant.courseId} onChange={(e) => setGrant({ ...grant, courseId: e.target.value })} className={fieldCls}>
                    <option value="">Select programme…</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                </div>
                <button disabled={busy === 'grant' || !grant.userId || !grant.courseId} className="lms-btn-primary">
                  {busy === 'grant' ? 'Granting…' : 'Grant access'}
                </button>
              </div>
              {grantMsg && (
                <p className="mt-3 rounded-xl border border-rule bg-paper px-3.5 py-2.5 text-[12px] leading-5 text-ink" role="status">{grantMsg}</p>
              )}
            </form>
          </Panel>

          <Panel className="overflow-x-auto">
            <PanelHead title="Student account" action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{students.length} profiles</span>} />
            <table className="w-full min-w-[840px]">
              <thead className="border-b border-rule bg-paper/60">
                <tr>
                  <th className={thCls}>Student</th>
                  <th className={thCls}>Role</th>
                  <th className={thCls}>Enrolments</th>
                  <th className={thCls}>Access</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {students.map((s) => (
                  <tr key={s.id} className="align-top hover:bg-paper/40">
                    <td className={tdCls}>
                      <p className="font-medium text-ink">{s.full_name || '(no name)'}</p>
                      {s.email && <p className="mt-0.5 text-[12px] text-ink-2">{s.email}</p>}
                      <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.08em] text-ink-2">
                        {s.id.slice(0, 8)}… {s.phone ? `· ${s.phone}` : ''}
                      </p>
                    </td>
                    <td className={tdCls}>
                      <Stamp tone={s.role === 'ADMIN' ? 'issued' : 'neutral'}>{s.role}</Stamp>
                    </td>
                    <td className={tdCls}>
                      {s.enrollments.length > 0 ? (
                        <ul className="space-y-1">
                          {s.enrollments.map((e) => (
                            <li key={e.course_id} className="flex flex-wrap items-center gap-2 text-[12px]">
                              <span className="text-ink">{courseTitle(e.course_id)}</span>
                              <Stamp tone={e.status === 'revoked' ? 'held' : 'cleared'}>{e.status}</Stamp>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-ink-2">—</span>
                      )}
                    </td>
                    <td className={tdCls}>
                      <div className="flex flex-wrap gap-1.5">
                        {s.enrollments.map((e) => (
                          <button
                            key={e.course_id}
                            onClick={async () => {
                              setBusy(`${s.id}:${e.course_id}`);
                              setError('');
                              try {
                                await setEnrollmentStatus(s.id, e.course_id, e.status === 'revoked' ? 'active' : 'revoked');
                                await refresh();
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'Could not change enrolment status.');
                              } finally {
                                setBusy('');
                              }
                            }}
                            disabled={busy === `${s.id}:${e.course_id}`}
                            className={e.status === 'revoked' ? 'lms-btn-secondary !px-2.5 !py-1.5 !text-[11px]' : 'lms-btn-danger !px-2.5 !py-1.5 !text-[11px]'}
                          >
                            {e.status === 'revoked' ? `Restore ${courseTitle(e.course_id)}` : `Revoke ${courseTitle(e.course_id)}`}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
                {students.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-10 text-center text-[12.5px] text-ink-2">
                      No student profiles returned. Check the Supabase profile policies.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Panel>
        </div>
      )}

      {tab === 'grading' && <GradingQueue />}

      {tab === 'commerce' && <CommerceAdmin />}

      {tab === 'announcements' && <AnnouncementsAdmin />}

      {tab === 'certificates' && <CertificatesAdmin />}

      {tab === 'reviews' && <ReviewsAdmin />}
    </div>
  );
};

export default AdminDashboard;
